import { Spinner } from '@components';
import ReviewMembersModal from '@features/group-management/components/review-members-modal/ReviewMembersModal';
import GroupDueDateConsent from '@features/group-management/components/v1/group-details-input/GroupDueDateConsent';
import BasicStep from '@features/group-management/components/v2/create-group/BasicStep';
import { getPaymentCollectionComponentType } from '@features/group-management/helper';
import useEditGroupForm from '@features/group-management/hooks/useEditGroupForm';
import ConfirmationModalV2 from '@layouts/modal/ConfirmationModal.v2';
import { PaymentCollectionComponentType } from '@types';

import EditGroupHeader from './EditGroupHeader';

const EditGroup = () => {
  const {
    formData,
    initialFormData,
    onInputChange,
    memberList,
    isFeeCategoryEnabled,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    feeDetailsError,
    setFeeDetailsError,
    bulkPaymentDiscountRowErrors,
    setBulkPaymentDiscountRowErrors,
    isSaveDisabled,
    isLoading,
    isPageLoading,
    onSaveEditGroup,
    hasGroupAttendanceEditAccess,
    handleAttendanceSwitchClick,
    groupId,
    isAttendanceBasedFee,
    hasChanges,
    showAutoDebitModal,
    setShowAutoDebitModal,
    onHandleAutoDebit,
    groupData,
    showAmountChangeModal,
    setShowAmountChangeModal,
    onHandleAmountChange,
    showReviewMembersModal,
    setShowReviewMembersModal,
    eligibleMembersData,
    onConfirmReviewMembers,
    isValidatingMembers,
    isPaymentLinkSentForGroup,
    setIsPaymentLinkSentForGroup,
    showDueDateConsent,
    setShowDueDateConsent,
    onDueDateConsentConfirm
  } = useEditGroupForm();

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-cofeeGreyBlue50">
      <EditGroupHeader
        groupId={groupId}
        onSave={onSaveEditGroup}
        isSaveDisabled={isSaveDisabled}
        isLoading={isLoading || isValidatingMembers}
        hasChanges={hasChanges}
      />
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <div className="mx-auto w-5/6 min-w-120 py-8">
          {isPageLoading ? (
            <div className="flex h-150 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <BasicStep
              formData={formData}
              onInputChange={onInputChange}
              memberList={memberList}
              isFeeCategoryEnabled={isFeeCategoryEnabled}
              setIsFeeCategoryEnabled={setIsFeeCategoryEnabled}
              feeCategories={feeCategories}
              setFeeCategories={setFeeCategories}
              branchDetails={branchDetails}
              customTermDueDateError={customTermDueDateError}
              setCustomTermDueDateError={setCustomTermDueDateError}
              feeDetailsError={feeDetailsError}
              setFeeDetailsError={setFeeDetailsError}
              bulkPaymentDiscountRowErrors={bulkPaymentDiscountRowErrors}
              onClearBulkPaymentDiscountRowErrors={() =>
                setBulkPaymentDiscountRowErrors([])
              }
              hasGroupAttendanceEditAccess={hasGroupAttendanceEditAccess}
              handleAttendanceSwitchClick={handleAttendanceSwitchClick}
              isLoading={isLoading}
              isAttendanceBasedFee={isAttendanceBasedFee}
              isEditGroup
              onPaymentLinkSentChange={setIsPaymentLinkSentForGroup}
            />
          )}
        </div>
      </div>
      {showAutoDebitModal && (
        <ConfirmationModalV2
          onCancel={() => setShowAutoDebitModal(false)}
          onConfirm={onHandleAutoDebit}
          title="Auto Debit"
          description={`${groupData?.auto_debit_enabled_default_member_count} autopay mandate for this group has a max limit below ₹${groupData?.group_member_mandate_max_amount}. This mandate will be cancelled if you proceed. Are you sure you want to continue?`}
          iconName="autopay"
          className="!w-105"
          confirmButtonLabel="Proceed"
        />
      )}
      {showAmountChangeModal && (
        <ConfirmationModalV2
          onCancel={() => setShowAmountChangeModal(false)}
          onConfirm={onHandleAmountChange}
          title="Rate per class updated"
          description="The updated rate per class will be applied for fee calculation in this billing cycle."
          iconName="warning-orange"
          className="!w-105"
        />
      )}
      {showReviewMembersModal && eligibleMembersData && (
        <ReviewMembersModal
          data={eligibleMembersData}
          formData={formData}
          initialFormData={initialFormData}
          onCancel={() => setShowReviewMembersModal(false)}
          onConfirm={onConfirmReviewMembers}
          isLoading={isLoading}
          isPaymentLinkSentForGroup={isPaymentLinkSentForGroup}
        />
      )}
      {showDueDateConsent && (
        <GroupDueDateConsent
          setShowConsent={setShowDueDateConsent}
          collectionType={
            (getPaymentCollectionComponentType(
              formData.paymentCollectionType,
              Number(formData.intervalFrequency)
            ) as PaymentCollectionComponentType) ||
            PaymentCollectionComponentType.MONTHLY
          }
          handleConsentConfirm={onDueDateConsentConfirm}
        />
      )}
    </div>
  );
};

export default EditGroup;
